import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SectionManager() {
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    section_type: 'popular',
    category_id: '',
    sort_order: 'date_desc',
    track_limit: '12',
    display_order: '0',
    is_active: true,
    require_auth: true
  });

  useEffect(() => {
    loadSections();
    loadCategories();
  }, []);

  const loadSections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/sections/admin`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSections(data);
    } catch (error) {
      console.error('Erreur chargement sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Toujours utiliser POST (OVH ne supporte pas PUT)
      const url = editingSection 
        ? `${BACKEND_URL}/api/sections/${editingSection.id}/update`
        : `${BACKEND_URL}/api/sections`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          category_id: formData.category_id || null,
          track_limit: Number(formData.track_limit),
          display_order: Number(formData.display_order)
        })
      });

      if (response.ok) {
        setShowModal(false);
        setEditingSection(null);
        resetForm();
        loadSections();
      }
    } catch (error) {
      console.error('Erreur sauvegarde section:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette section ?')) return;

    try {
      const token = localStorage.getItem('token');
      // Utilise POST au lieu de DELETE pour compatibilité OVH mutualisé
      const response = await fetch(`${BACKEND_URL}/api/sections/${id}/delete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        loadSections();
      }
    } catch (error) {
      console.error('Erreur suppression section:', error);
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
      setFormData({
        title: section.title,
        description: section.description || '',
        section_type: section.section_type,
        category_id: section.category_id || '',
        sort_order: section.sort_order,
        track_limit: String(section.track_limit ?? '12'),
        display_order: String(section.display_order ?? '0'),
        is_active: section.is_active,
        require_auth: section.require_auth
      });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      section_type: 'popular',
      category_id: '',
      sort_order: 'date_desc',
      track_limit: '12',
      display_order: '0',
      is_active: true,
      require_auth: true
    });
  };

  const getSectionTypeLabel = (type) => {
    const types = {
      'popular': 'Musiques populaires',
      'by_category': 'Par catégorie',
      'new_releases': 'Nouveautés',
      'random': 'Aléatoire',
      'user_preferences': 'Recommandations',
      'all': 'Toutes les musiques'
    };
    return types[type] || type;
  };

  const getSortOrderLabel = (order) => {
    const orders = {
      'random': 'Aléatoire',
      'date_desc': 'Plus récent → ancien',
      'date_asc': 'Plus ancien → récent',
      'streams_desc': 'Plus écouté → moins écouté',
      'alphabetical': 'Alphabétique'
    };
    return orders[order] || order;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">Gestion des Sections</h2>
          <p className="text-sm text-white/60 mt-1">Créez des sections dynamiques pour la page d'accueil</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingSection(null);
            setShowModal(true);
          }}
          className="px-4 py-2 bg-green-600 rounded-full hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <Plus size={18} />
          Nouvelle Section
        </button>
      </div>

      {/* Liste des sections */}
      {loading && sections.length === 0 ? (
        <div className="text-center py-12 text-white/60">Chargement...</div>
      ) : sections.length === 0 ? (
        <div className="text-center py-12 text-white/60">
          Aucune section créée. Cliquez sur "Nouvelle Section" pour commencer.
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((section) => (
            <div
              key={section.id}
              className="bg-white/5 rounded-lg p-4 border border-white/10 hover:border-white/20 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{section.title}</h3>
                    {section.is_active ? (
                      <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs flex items-center gap-1">
                        <Eye size={12} /> Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-red-600/20 text-red-400 rounded text-xs flex items-center gap-1">
                        <EyeOff size={12} /> Inactive
                      </span>
                    )}
                    {section.require_auth && (
                      <span className="px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-xs">
                        🔒 Connecté uniquement
                      </span>
                    )}
                  </div>
                  
                  {section.description && (
                    <p className="text-sm text-white/60 mb-3">{section.description}</p>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-white/40">Type:</span>
                      <div className="font-medium">{getSectionTypeLabel(section.section_type)}</div>
                    </div>
                    
                    {section.section_type === 'by_category' && section.category_name && (
                      <div>
                        <span className="text-white/40">Catégorie:</span>
                        <div className="font-medium">{section.category_name}</div>
                      </div>
                    )}
                    
                    <div>
                      <span className="text-white/40">Ordre:</span>
                      <div className="font-medium">{getSortOrderLabel(section.sort_order)}</div>
                    </div>
                    
                    <div>
                      <span className="text-white/40">Nombre:</span>
                      <div className="font-medium">{section.track_limit} musiques</div>
                    </div>
                    
                    <div>
                      <span className="text-white/40">Position:</span>
                      <div className="font-medium">#{section.display_order}</div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(section)}
                    className="p-2 bg-blue-600/20 text-blue-400 rounded hover:bg-blue-600/30 transition-colors"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(section.id)}
                    className="p-2 bg-red-600/20 text-red-400 rounded hover:bg-red-600/30 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Création/Édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 modal-text">
          <div className="bg-neutral-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-neutral-900 border-b border-white/10 p-6">
              <h3 className="text-xl font-bold">
                {editingSection ? 'Modifier la Section' : 'Nouvelle Section'}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Titre</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:outline-none"
                  placeholder="Ex: Musiques populaires"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:outline-none"
                  placeholder="Ex: Les titres les plus écoutés"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Type de contenu</label>
                <select
                  value={formData.section_type}
                  onChange={(e) => setFormData({ ...formData, section_type: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:outline-none"
                >
                  <option value="popular">Musiques populaires (plus streamées)</option>
                  <option value="by_category">Par catégorie spécifique</option>
                  <option value="new_releases">Nouveautés (derniers ajouts)</option>
                  <option value="random">Aléatoire</option>
                  <option value="user_preferences">Recommandations (selon goûts utilisateur)</option>
                  <option value="all">Toutes les musiques</option>
                </select>
              </div>

              {formData.section_type === 'by_category' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Catégorie</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:outline-none"
                  >
                    <option value="">Sélectionnez une catégorie</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Ordre d'affichage</label>
                <select
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:outline-none"
                >
                  <option value="random">Aléatoire</option>
                  <option value="date_desc">Plus récent → ancien</option>
                  <option value="date_asc">Plus ancien → récent</option>
                  <option value="streams_desc">Plus écouté → moins écouté</option>
                  <option value="alphabetical">Alphabétique</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nombre de musiques</label>
                  <select
                    value={formData.track_limit}
                    onChange={(e) => setFormData({ ...formData, track_limit: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:outline-none"
                  >
                    <option value="6">6 musiques</option>
                    <option value="12">12 musiques</option>
                    <option value="18">18 musiques</option>
                    <option value="24">24 musiques</option>
                    <option value="100">100 musiques</option>
                    <option value="200">200 musiques</option>
                    <option value="300">300 musiques</option>
                    <option value="400">400 musiques</option>
                    <option value="500">500 musiques</option>
                    <option value="600">600 musiques</option>
                    <option value="700">700 musiques</option>
                    <option value="800">800 musiques</option>
                    <option value="900">900 musiques</option>
                    <option value="1000">1000 musiques</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Position</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-green-500 focus:outline-none"
                    min="0"
                  />
                  <p className="text-xs text-white/40 mt-1">Plus petit = plus haut sur la page</p>
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Section active</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.require_auth}
                    onChange={(e) => setFormData({ ...formData, require_auth: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Uniquement pour utilisateurs connectés</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingSection(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Enregistrement...' : editingSection ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
